import React, { useState, useRef, useEffect } from "react";

interface FilterSelectProps {
  options: string[];
  label?: string;
  className?: string;
  value: string;
  name: string;
  handleChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  onBlur?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  optionHoverBg?: string;
  optionHoverText?: string;
  optionSelectedBg?: string;
  optionSelectedText?: string;
  [key: string]: any;
}

function FilterSelect({
  options,
  label,
  className = "",
  value,
  handleChange,
  onBlur,
  name,
  optionHoverBg = "bg-[#222F3C]",
  optionHoverText = "text-[#55B7E4]",
  optionSelectedBg = "bg-[#222F3C]",
  optionSelectedText = "text-[#55B7E4]",
  ...props
}: FilterSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownButtonRef = useRef<HTMLButtonElement>(null);

  const textSizeClasses =
    "xs:text-[9px] sm:text-[11px] md:text-[13px] lg:text-[15px] xl:text-[15px]";

  const handleSelect = (option: string) => {
    setSelectedValue(option);
    setIsOpen(false);
    handleChange({
      target: { name, value: option },
    } as React.ChangeEvent<HTMLSelectElement>);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownButtonRef.current &&
        dropdownButtonRef.current.contains(event.target as Node)
      ) {
        return;
      }
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  return (
    <div className="relative flex flex-col items-start w-full">
      {label && (
        <label
          className={`${textSizeClasses} text-[#889DB2] font-semibold mb-1`}
        >
          {label}
        </label>
      )}

      {/* Custom select button */}
      <button
        ref={dropdownButtonRef}
        type="button"
        className={`
          px-3 py-2 rounded-lg outline-none bg-transparent border border-gray-600 
          text-left w-full flex items-center justify-between
          ${textSizeClasses} ${className}
        `}
        // onClick={() => setIsOpen(!isOpen)}
        onClick={() => setIsOpen((pre) => !pre)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span>{selectedValue}</span>
        <svg
          className={`w-4 h-4 ml-2 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Custom dropdown options */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className={`
            absolute top-full left-0 right-0 z-10 mt-1 max-h-60 overflow-auto
            bg-[#2E4051] border border-gray-600 rounded-lg shadow-lg
            ${textSizeClasses}
            scrollbar-hide
          `}
          role="listbox"
        >
          {options.map((option) => (
            <div
              key={option}
              className={`
                px-3 py-2 cursor-pointer transition-colors
                ${
                  selectedValue === option
                    ? `${optionSelectedBg} ${optionSelectedText}`
                    : "text-white"
                }
                  hover:bg-[#222F3C] 
                  hover:${optionHoverText}
              `}
              // hover:bg-[#008564] hover:${optionHoverBg}
              onClick={() => handleSelect(option)}
              role="option"
              aria-selected={selectedValue === option}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default FilterSelect;
